import { matchesSearchText } from '@/utils/searchText'
import type { GuidedAnswer, GuidedModeSymptom, GuidedModeSymptomEntry } from '../types/guided-mode.types'
import { DEMOGRAPHIC_CONCEPT_IDS } from './assessment-definition'
import { isStepVisible } from './step-engine'

type Answers = Record<string, GuidedAnswer>

export const OTHER_SYMPTOM = '__other__'

export interface GuidedModeSymptomChip {
  id: string
  label: string
}

export function demographicAnswers(answers: Answers): Answers {
  return Object.fromEntries(Object.entries(answers).filter(([conceptId]) => DEMOGRAPHIC_CONCEPT_IDS.has(conceptId)))
}

// Only demographics are known on Symptoms, so later answers never change what is offered or recorded.
export function recordedEntry(
  symptom: GuidedModeSymptom,
  answers: Answers,
): GuidedModeSymptomEntry | undefined {
  const known = demographicAnswers(answers)
  return [symptom, ...(symptom.alternatives || [])]
    .find(entry => isStepVisible({ visibleWhen: entry.visibleWhen }, known))
}

export function availableSymptoms(symptoms: GuidedModeSymptom[], answers: Answers): GuidedModeSymptom[] {
  return symptoms.filter(symptom => recordedEntry(symptom, answers))
}

export function searchSymptoms(
  symptoms: GuidedModeSymptom[],
  query: string,
  label: (symptom: GuidedModeSymptom) => string,
): GuidedModeSymptom[] {
  return symptoms.filter(symptom => matchesSearchText(label(symptom), query))
}

export function isSymptomRecorded(symptom: GuidedModeSymptom, answers: Answers): boolean {
  if (symptom.selectionOnly) return true
  const entry = recordedEntry(symptom, answers)
  return entry !== undefined && answers[entry.conceptId] === entry.value
}

// Each symptom carries the code and concept it was recorded on, e.g. newborn Jaundice after day 1.
export function recordedSymptoms(
  trackSymptoms: GuidedModeSymptom[],
  recordedIds: string[],
  answers: Answers,
): GuidedModeSymptom[] {
  return trackSymptoms.flatMap(symptom => {
    const entry = recordedEntry(symptom, answers)
    if (!entry || !recordedIds.includes(symptom.id)) return []
    if (!symptom.selectionOnly && answers[entry.conceptId] !== entry.value) return []
    return [entry === symptom
      ? symptom
      : { ...symptom, ...entry, visibleWhen: entry.visibleWhen, alternatives: undefined }]
  })
}

// previousAnswers: the answers before a demographics edit, under which another entry may be recorded.
export function recordSymptoms(input: {
  answers: Answers
  previousAnswers?: Answers
  trackSymptoms: GuidedModeSymptom[]
  available: GuidedModeSymptom[]
  selectedIds: string[]
  recordedIds: string[]
}): { answers: Answers; selectedIds: string[]; recordedIds: string[] } {
  const availableIds = new Set(input.available.map(symptom => symptom.id))
  const selectedIds = input.selectedIds.filter(id => availableIds.has(id))
  const selected = new Set(selectedIds)
  const answers = { ...input.answers }
  const nextEntry = (symptom: GuidedModeSymptom) => (
    selected.has(symptom.id) ? recordedEntry(symptom, input.answers) : undefined
  )
  for (const symptom of input.trackSymptoms) {
    if (symptom.selectionOnly) continue
    if (!input.recordedIds.includes(symptom.id)) continue
    const entry = recordedEntry(symptom, input.previousAnswers || input.answers) || symptom
    if (entry !== nextEntry(symptom) && answers[entry.conceptId] === entry.value) {
      delete answers[entry.conceptId]
    }
  }
  for (const symptom of input.available) {
    const entry = nextEntry(symptom)
    if (entry && !symptom.selectionOnly) answers[entry.conceptId] = entry.value
  }
  return { answers, selectedIds, recordedIds: selectedIds }
}
